import { useQuery, useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api/client';
import Code from '../components/Code';
import HeadlineBand from '../components/engine/HeadlineBand';

const ASSET_LABEL: Record<string, string> = {
  copacker: 'Co-packer',
  water_pet_supplier: 'Water / PET',
  freight_3pl: 'Freight / 3PL',
  packaging: 'Packaging',
  logistics_hub: 'Logistics hub',
};

export default function Engine6_MnA() {
  const targets = useQuery({ queryKey: ['mna', 'targets'], queryFn: api.mnaTargets });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const model = useMutation({
    mutationFn: (id: string) => api.mnaModel(id),
  });

  function pickTarget(id: string) {
    setSelectedId(id);
    model.mutate(id);
  }
  const headlineState = { targets: targets.data ?? [], ...(model.data ?? {}) };

  return (
    <div>
      {targets.data && <HeadlineBand view="capacity_acquisition" state={headlineState} />}

      <div className="grid2">
        <div className="panel">
          <div className="qhead"><span className="qdot" style={{ background: 'var(--blue)' }} />Target screen · {targets.data?.length ?? 0} candidates</div>
          {targets.data?.map((t) => (
            <div
              key={t.id}
              onClick={() => pickTarget(t.id)}
              style={{
                padding: '12px 14px',
                marginBottom: 6,
                borderRadius: 8,
                cursor: 'pointer',
                background: selectedId === t.id ? 'var(--panel2)' : 'transparent',
                border: selectedId === t.id ? '1px solid var(--amber)' : '1px solid var(--line)',
                transition: 'border-color 0.15s ease',
              }}
            >
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{t.name ?? t.id}</div>
                  <div className="small faint" style={{ marginTop: 2 }}>
                    {ASSET_LABEL[t.asset_type] ?? t.asset_type} · {t.location ?? '—'}
                  </div>
                </div>
                <div className="row" style={{ gap: 10 }}>
                  <span className={`tag ${t.fit_score >= 80 ? 'olive' : t.fit_score >= 70 ? 'gold' : 'muted'}`}>
                    fit {t.fit_score}
                  </span>
                </div>
              </div>
              <div className="small" style={{ marginTop: 6 }}>
                {t.capacity} · freight gain {t.freight_gain}
              </div>
            </div>
          ))}
        </div>

        <div className="panel">
          <div className="qhead"><span className="qdot" style={{ background: 'var(--amber)' }} />Capacity / freight model</div>
          {!selectedId && (
            <div className="small">Click a target on the left to model it.</div>
          )}
          {selectedId && (model.isPending ? (
            <div className="small faint">Modeling…</div>
          ) : model.data ? (
            <>
              <div className="field">
                <div className="field-label">Capacity gain</div>
                <div className="field-value">{model.data.capacity_gain}</div>
              </div>
              <div className="field">
                <div className="field-label">Freight saving (annual)</div>
                <div className="field-value" style={{ color: 'var(--olive)' }}>{model.data.freight_saving}</div>
              </div>
              <div className="field">
                <div className="field-label">Payback</div>
                <div className="field-value">{model.data.payback}</div>
              </div>
              <div className="field" style={{ borderBottom: 'none' }}>
                <div className="field-label">Recommendation</div>
                <div className="field-value">
                  {model.data.recommendation.startsWith('GO') ? (
                    <span className="tag olive">{model.data.recommendation}</span>
                  ) : (
                    <span className="tag gold">{model.data.recommendation}</span>
                  )}
                </div>
              </div>
              <div className="divider" />
              <div className="small faint">
                Non-objective guard: <span className="tag red"><Code id="N4" kind="non_objective" compact /></span> no brand whiplash · <span className="tag red"><Code id="N5" kind="non_objective" compact /></span> no surrender of control.
                Asset-only acquisitions allowed.
              </div>
            </>
          ) : (
            <div className="small">Click a target on the left to model it.</div>
          ))}
        </div>
      </div>
    </div>
  );
}
